import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Loader } from '@buffer-mono/popcorn'

import {
  preloadComposerDialog,
  usePostComposer,
} from '~publish/hooks/usePostComposer'
import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { useAppSelector } from '~publish/legacy/store'

import styles from '~publish/styles/centered-loader.module.css'

import formatPostFromQuery from './formatPostFromQuery'

export interface InitComposerArgs {
  post: PrefillPostData
  preselectedChannelIds: string[]
}

const ComposePage = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const [composerWasOpened, setComposerWasOpened] = useState<boolean>(false)
  const hasOpenedComposerRef = useRef(false)
  const { isOpen, createNewPostInComposer } = usePostComposer()
  const orgsLoaded = useAppSelector((state) => state.organizations.loaded)
  const profilesLoaded = useAppSelector((state) => state.profileSidebar.loaded)
  const hasCurrentOrg = !!useAppSelector(
    (state) => state.organizations?.selected,
  )

  const navigate = useNavigate()

  const isLoading = !orgsLoaded || !profilesLoaded || !hasCurrentOrg

  // This route always opens the composer, so preload its chunk in parallel
  // with the data load below instead of waiting on it.
  useEffect(() => {
    preloadComposerDialog()
  }, [])

  useEffect(() => {
    if (isLoading || hasOpenedComposerRef.current) return

    hasOpenedComposerRef.current = true
    const rawQuery = searchParams.toString()
    const queryParams = rawQuery ? `?${rawQuery}` : ''
    const prefillPostData: PrefillPostData = formatPostFromQuery(queryParams)

    void createNewPostInComposer({
      prefillPostData,
      channels: [],
      cta: SEGMENT_NAMES.EXTENSION_CREATE_A_POST,
    })
    // We only want to open the composer once on mount, not when createNewPostInComposer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, searchParams])

  useEffect(() => {
    if (isOpen) {
      setComposerWasOpened(true)
    }

    if (!isOpen && composerWasOpened) {
      setComposerWasOpened(false)
      // Close the window if it was opened by the Chrome extension
      // This will silently fail if the window was not opened via window.open()
      window.close()
      // Navigate as a fallback after a short delay — if window.close()
      // succeeded the JS context is gone and this never runs
      setTimeout(() => {
        navigate('..', { replace: true })
      }, 500)
    }
  }, [isOpen, composerWasOpened, navigate])

  return (
    <>
      {isLoading && (
        <div className={styles.centeredLoader}>
          <Loader />
        </div>
      )}
    </>
  )
}

export default ComposePage
